import {
  AssetSchedule,
  ChargingProfile,
  GetEnergyScheduleResponse,
  GetMeasurementsForSiteResponse,
  GetSiteResponse,
  Measurement,
  DayAheadPrices,
} from "../../../services/client";
import { TimeSeries } from "../Charts/TimeSeriesChart";

export const extrapolateToTimeseries = (
  timeseries: TimeSeries,
  datetimes: Date[],
  method: "forward" | "null" = "forward",
): TimeSeries => {
  const originalMap = new Map(
    timeseries.timestamps.map((t, i) => [t.getTime(), timeseries.values[i]]),
  );

  let valueToUse: number | null = null;

  // We forward fill the last value if we don't have a value for a given timestamp
  const values = datetimes.map((datetime) => {
    const value = originalMap.get(datetime.getTime());
    if (value !== undefined) {
      if (method === "forward") {
        valueToUse = value;
      }
      return value;
    } else {
      return valueToUse;
    }
  });

  return {
    timestamps: datetimes,
    values,
    name: timeseries.name,
    color: timeseries.color,
  };
};

export const parseMeasurementResponse = (
  response: GetMeasurementsForSiteResponse,
): TimeSeries[] => {
  const groupedPerAsset = response.reduce(
    (acc: { [assetId: string]: Measurement[] }, measurement) => {
      if (!acc[measurement.source.asset_id]) {
        acc[measurement.source.asset_id] = [];
      }
      acc[measurement.source.asset_id].push(measurement);
      return acc;
    },
    {} as { [assetId: string]: Measurement[] },
  );

  const data = Object.entries(groupedPerAsset).map(([assetId, measurements]) =>
    mapMeasurementsToTimeSeries(measurements, assetId),
  );

  return data;
};

export const mapMeasurementsToTimeSeries = (
  measurements: Measurement[],
  assetId: string,
): TimeSeries => {
  return {
    timestamps: measurements.map(
      (measurement) => new Date(measurement.timestamp),
    ),
    values: measurements.map((measurement) => measurement.powerValue),
    name: assetId,
  };
};

export const parseScheduleWithMeasurementsToTimeSeries = (
  response: GetEnergyScheduleResponse,
  measurements: Measurement[],
): TimeSeries[] => {
  return pushMeasurementsIntoTimeSeries(
    response.schedules.map((schedule) => mapScheduleToTimeSeries(schedule)),
    measurements,
  );
};

export const parseScheduleWithMeasurementsToCumulativeEnergyTimeSeries = (
  response: GetEnergyScheduleResponse,
  measurements: Measurement[],
): TimeSeries[] => {
  const powerSeries = parseScheduleWithMeasurementsToTimeSeries(
    response,
    measurements,
  );
  return powerSeries.map(integratePowerToCumulativeEnergy);
};

const integratePowerToCumulativeEnergy = (series: TimeSeries): TimeSeries => {
  const sortedIndices = series.timestamps
    .map((_, i) => i)
    .sort(
      (a, b) => series.timestamps[a].getTime() - series.timestamps[b].getTime(),
    );
  const sortedTimestamps = sortedIndices.map((i) => series.timestamps[i]);
  const sortedValues = sortedIndices.map((i) => series.values[i]);

  const cumulativeValues: number[] = [];
  let cumulativeWh = 0;
  for (let i = 0; i < sortedTimestamps.length; i++) {
    if (i > 0) {
      const dtHours =
        (sortedTimestamps[i].getTime() - sortedTimestamps[i - 1].getTime()) /
        3_600_000;
      const powerW = sortedValues[i - 1] ?? 0;
      cumulativeWh += powerW * dtHours;
    }
    cumulativeValues.push(cumulativeWh / 1000);
  }

  return {
    name: series.name,
    color: series.color,
    timestamps: sortedTimestamps,
    values: cumulativeValues,
  };
};

export const parseDayAHeadPricesToTimeSeries = (
  priceTimeSeries: DayAheadPrices,
): TimeSeries => {
  return Object.entries(priceTimeSeries.data).reduce(
    (acc, [_, point]) => {
      acc.timestamps.push(new Date(point.timestamp));
      acc.values.push(point.value);
      return acc;
    },
    { timestamps: [], values: [], name: "Day a head prices" } as TimeSeries,
  );
};
export const pushMeasurementsIntoTimeSeries = (
  timeSeries: TimeSeries[],
  measurements: Measurement[],
): TimeSeries[] => {
  const timeSeriesByName = Object.fromEntries(
    timeSeries.map((ts) => [ts.name, ts]),
  );
  for (const measurement of measurements) {
    const timeserie = timeSeriesByName[measurement.source.asset_id];
    if (!timeserie) {
      timeSeriesByName[measurement.source.asset_id] = {
        name: measurement.source.asset_id,
        values: [measurement.powerValue],
        timestamps: [new Date(measurement.timestamp)],
      };
      continue;
    }

    timeserie?.timestamps.push(new Date(measurement.timestamp));
    timeserie?.values.push(measurement.powerValue);
  }

  return Object.values(timeSeriesByName);
};

export const mapScheduleToTimeSeries = (
  schedule: AssetSchedule,
): TimeSeries => {
  const chargingProfile = schedule.profile.filter(
    (point): point is ChargingProfile => point.type !== "grid",
  );
  return {
    timestamps: chargingProfile.map((point) => new Date(point.timestamp)),
    values: chargingProfile.map((point) => point.powerValue),
    name: schedule.assetId.replace(".P_charging", ""), // Can be removed once we have fixed this in the backend.
  };
};

export const getAllAssetIds = (data: GetSiteResponse) => {
  const site_configuration = data.configurations?.site_configuration;

  if (!site_configuration) {
    return [];
  }

  return site_configuration["device_list"]
    ?.filter(
      (device) =>
        device["type"] === "charging_pole_3phase" ||
        device["type"] === "battery_energy_storage_system",
    )
    .map((device) => device["name"]);
};

export const generateCommonTimeline = (series: TimeSeries[]): Date[] => {
  const timestampSet = new Set<number>();
  for (const s of series) {
    s.timestamps.forEach((t) => timestampSet.add(t.getTime()));
  }
  return Array.from(timestampSet)
    .sort((a, z) => a - z)
    .map((t) => new Date(t));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseArrowToTimeSeries = (table: any[]): TimeSeries[] => {
  if (!table) {
    return [];
  }

  const rowCount = table.length;
  const grouped: Record<string, { timestamps: Date[]; values: number[] }> = {};
  for (let i = 0; i < rowCount; i++) {
    const id = table[i].source.asset_id.toString();
    const ts = new Date(table[i].timestamp);
    const value = Number(table[i].powerValue);

    grouped[id] ??= { timestamps: [], values: [] };

    grouped[id].timestamps.push(ts);
    grouped[id].values.push(value);
  }

  const result: TimeSeries[] = Object.entries(grouped).map(([name, data]) => ({
    name,
    timestamps: data.timestamps,
    values: data.values,
  }));

  return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const parseArrowToSOCTimeSeries = (table: any[]): TimeSeries[] => {
  if (!table) {
    return [];
  }

  const rowCount = table.length;
  const grouped: Record<
    string,
    { timestamps: Date[]; values: (number | null)[] }
  > = {};
  for (let i = 0; i < rowCount; i++) {
    const soc = table[i].currentStateOfCharge;
    if (soc == null) {
      continue;
    }
    const id = table[i].source.asset_id.toString();
    const ts = new Date(table[i].timestamp);

    grouped[id] ??= { timestamps: [], values: [] };
    grouped[id].timestamps.push(ts);
    grouped[id].values.push(Number(soc));
  }

  return Object.entries(grouped).map(([name, data]) => ({
    name,
    timestamps: data.timestamps,
    values: data.values,
  }));
};
